import { apiService } from "./api.service";
import { Language } from "types/index";
import { Lookup } from "types/lookup";
import { PaginatedResponse } from "types/index";
import api_no_token_Service from "./api_no_token.service";

export interface LookupDto {
  Id?: number;
  id?: number;
  LookupId?: number;
  lookupId?: number;
  Code?: string;
  code?: string;
  Name?: string;
  name?: string;
  NameEN?: string;
  nameEN?: string;
  IsDraft?: boolean;
  isDraft?: boolean;
}

class LookupService {
  async createDraft(payload: Partial<Lookup>): Promise<Lookup> {
    const response = await api_no_token_Service.post<Lookup>(
      `/lookup/CreateDraft`,
      payload
    );
    if ((response as { data?: Lookup })?.data && (response as { success?: boolean })?.success !== false) {
      return (response as { data: Lookup }).data;
    }
    return response as unknown as Lookup;
  }

  async create(payload: Partial<Lookup>): Promise<Lookup> {
    const response = await apiService.post<Lookup>(`/lookup`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Lookup;
    }
    return response as any as Lookup;
  }

  async update(id: string, payload: Partial<Lookup>): Promise<Lookup> {
    const response = await apiService.put<Lookup>(`/lookup/${id}`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Lookup;
    }
    return response as any as Lookup;
  }

  async getByCode(code: string): Promise<LookupDto | null> {
    const res = await api_no_token_Service.get<LookupDto>(
      `/lookup/ByCode/${encodeURIComponent(code)}`
    );
    if ((res as { data?: LookupDto })?.data && (res as { success?: boolean })?.success !== false) {
      return (res as { data: LookupDto }).data;
    }
    return (res as LookupDto) ?? null;
  }

  async getByParentCode(
    parentCode: string,
    languageId: Language = Language.ENGLISH,
    includeLookupId?: string | number | null
  ): Promise<LookupDto[]> {
    const params = new URLSearchParams();
    params.set("languageId", String(languageId));
    if (
      includeLookupId !== undefined &&
      includeLookupId !== null &&
      String(includeLookupId) !== ""
    ) {
      params.set("includeLookupIds", String(includeLookupId));
    }
    const res = await api_no_token_Service.get<LookupDto[]>(
      `/lookup/GetByParentCode/${encodeURIComponent(parentCode)}?${params.toString()}`
    );
    if ((res as any)?.data && (res as any)?.success !== false) {
      return (res as any).data as LookupDto[];
    }
    return res as any as LookupDto[];
  }

  async getPageList(
    page: number = 1,
    limit: number = 100,
    languageId: number = 1,
    options?: {
      search?: string,
      parentLookupId?: string | number,
    }
  ): Promise<PaginatedResponse<Lookup>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(limit),
      languageId: String(languageId),
    });
    if (options?.search?.trim()) {
      params.set("search", options.search.trim());
    }
    if (options?.parentLookupId != null && String(options.parentLookupId).trim() !== "") {
      params.set("parentLookupId", String(options.parentLookupId));
    }
    const response = await apiService.get<PaginatedResponse<Lookup>>(
      `/lookup/GetPageList?${params.toString()}`
    );

    if (
      (response as any)?.data &&
      (response as any)?.success !== false &&
      (response as any).data?.data &&
      (response as any).data?.pagination
    ) {
      return (response as any).data as PaginatedResponse<Lookup>;
    }
    const maybeItems = (response as any)?.items;
    const maybeTotal = (response as any)?.totalCount;
    if (Array.isArray(maybeItems) && typeof maybeTotal === "number") {
      const totalPages = Math.max(1, Math.ceil(maybeTotal / limit));
      return {
        data: maybeItems as Lookup[],
        pagination: { page, limit, total: maybeTotal, totalPages },
      };
    }

    const data = (response as any)?.data || response;
    if (Array.isArray(data)) {
      return {
        data: data as Lookup[],
        pagination: { page, limit, total: data.length, totalPages: 1 },
      };
    }

    throw new Error((response as any)?.message || "Failed to load lookups");
  }

  async getById(id: string): Promise<Lookup> {
    const response = await apiService.get<any>(`/lookup/${id}`);
    const payload: any = (response as any)?.data ?? response;

    const lookupData =
      payload?.data && typeof payload.data === "object"
        ? payload.data
        : typeof payload === "object"
        ? payload
        : null;

    if (lookupData) {
      return { ...lookupData } as Lookup;
    }

    return response as unknown as Lookup;
  }

  async getAll(): Promise<LookupDto[]> {
    const res = await apiService.get<LookupDto[]>(`/lookup`);
    if ((res as any)?.data && (res as any)?.success !== false) {
      return (res as any).data as LookupDto[];
    }
    return res as any as LookupDto[];
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/lookup/${id}`);
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || "Delete failed");
    }
  }

  async toggleDraft(id: string): Promise<void> {
    const response = await apiService.put(`/lookup/${id}/ToggleDraft`, {});
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || "Toggle draft failed");
    }
  }

  async getSiblings(id: string): Promise<{ id: number; code: string; value: string; isDraft?: boolean }[]> {
    const response = await apiService.get<any>(`/lookup/${id}/Siblings`);
    const data = (response as any)?.data ?? response;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }

  async replace(id: string, replaceWithId: number): Promise<{ updatedCount: number }> {
    const response = await apiService.put<any>(`/lookup/${id}/Replace`, { replaceWithId });
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || "Replace failed");
    }
    const payload = (response as any)?.data ?? response;
    return { updatedCount: payload?.updatedCount ?? 0 };
  }
}

export const lookupService = new LookupService();
export default lookupService;
